import React, {createContext, useContext} from 'react';
import {Form, FormikProvider, useFormik} from 'formik';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {SHA256} from 'crypto-js';
import {useDispatch} from 'react-redux';
import {useGlobalContext} from '../../../../common/components/core/GlobalContext';
import {useGlobalMessageContext} from '../../../../common/components/core/GlobalMessageContext';
import {useMessage} from '../../../../common/components/core/useGlobalMessage';
import {useAuthContext} from '../../../../common/components/core/AuthContext';
import {pathnames as P} from '../../../../common/enums';
import {isEmail, isPhoneNumber} from '../../../../common/utils';
import {getErrorMessage} from '../../../../common/utils';
import {AppDispatch} from '../../../../store/configureStore';
import {setToken, setUser} from '../..';
import {trackEvent} from "../../../../common/utils/trackEvent";

export type RegisterContext = {[key: string]: never};

export type RegisterContextProviderProps = {
  children: React.ReactNode,
}

export const defaultContext: RegisterContext = {};

export type RegisterFormValues = {
  email: string,
  phone: string,
  password: string,
  confirmPassword: string,
}

export const defaultFormValues: RegisterFormValues = {
  email: '',
  phone: '',
  password: '',
  confirmPassword: '',
};

export const RegisterContext = createContext<RegisterContext>(defaultContext);
RegisterContext.displayName = 'RegisterContext';

export const useRegisterContext = () => useContext(RegisterContext);

export const RegisterContextProvider = ({children}: RegisterContextProviderProps) => {
  const {apiService} = useGlobalContext();
  const globalMessage = useGlobalMessageContext();
  const E = useMessage();
  const {handleLogin} = useAuthContext();
  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const guid = searchParams.get('guid');
  const affiliate = searchParams.get('affiliate');

  const formik = useFormik<RegisterFormValues>({
    initialValues: {...defaultFormValues},
    validate: (values) => {
      const errors: Partial<{ [key in keyof RegisterFormValues]: string }> = {};
      if (!values.email) errors.email = E.requiredField;
      if (values.email && !isEmail(values.email)) errors.email = E.notValidEmail;
      if (!values.phone) errors.phone = E.requiredField;
      if (values.phone && !isPhoneNumber(values.phone)) errors.phone = E.notValidPhoneNumber;
      if (!values.password) errors.password = E.requiredField;
      if (!values.confirmPassword) errors.confirmPassword = E.requiredField;
      if (values.password !== values.confirmPassword) errors.confirmPassword = E.textPasswordMismatch;

      return errors;
    },
    onSubmit: async (values) => {
      try {
        const encryptedPassword = SHA256(values.password);
        const result = await apiService.register(
          values.email,
          values.phone,
          encryptedPassword.toString(),
          guid,
          affiliate,
        );
        const token = result.body.token;
        const user = result.body.user;
        if (result.errorCode < 0) {
          globalMessage.open(getErrorMessage(result.errorCode, E));
        } else {
          if (token) {
            dispatch(setToken(token));
          }
          if (user) {
            dispatch(setUser(user));
          }

          trackEvent('sign_up', {method: 'User client registration'});

          await handleLogin();
          navigate(P.HOME);
        }
      } catch (e) {
        globalMessage.open(E.serverError);
      }
    },
    enableReinitialize: true,
  });

  const context: RegisterContext = {
    ...defaultContext,
  };

  return (
    <RegisterContext.Provider value={context}>
      <FormikProvider value={formik}>
        <Form>
          {children}
        </Form>
      </FormikProvider>
    </RegisterContext.Provider>
  );
};
